/**
 * Page Prompts Fetcher
 *
 * Fetches contextual prompts from the backend based on current route
 */

const API_BASE = process.env.REACT_APP_API_BASE_URL;

// Map frontend routes to backend page_name values
const ROUTE_TO_PAGE_NAME = {
  "/growth-suite": "GROWTH_SUITE",
  "/profile": "PROFILE",
  "/brand-dashboard": "BRAND_DASHBOARD",
  "/community-forum": "COMMUNITY_FORUM",
  "/suspire-circle": "SUSPIRE_CIRCLE",
  "/brands": "PRODUCT_LISTING",
  "/ssu-chatbot": "ssu",
  "/seller-university": "ssu",
  "/suspire-support-network": "SUSPIRE_SUPPORT_NETWORK",
  "/sustainability": "3SC_CERTIFICATION",
  "/sustainability-progress": "3SC_CERTIFICATION",
  "/sustainability-certified": "3SC_CERTIFICATION",
  "/subscriptions": "SUBSCRIPTIONS",
  "/pricing": "SUBSCRIPTIONS",
  "/wallet": "SUBSCRIPTIONS",
  // Additional common routes
  "/growth": "GROWTH_SUITE",
  "/3sc": "3SC_CERTIFICATION",
  "/community": "COMMUNITY_FORUM",
  // Every Terminal screen shares one set of prompts (backend page_name choice).
  "/suspire-terminal": "suspire_terminal",
};

const TERMINAL_PAGE_NAME = "suspire_terminal";

/**
 * Get page prompts for the current route
 * @param {string} pathname - Current window.location.pathname
 * @returns {Promise<Array>} Array of prompt objects
 */
export async function getPagePrompts(pathname) {
  // Normalize pathname to match our route map
  const normalizedPath = normalizePathname(pathname);
  const pageName = ROUTE_TO_PAGE_NAME[normalizedPath];
  const fallbackPrompts =
    pageName === TERMINAL_PAGE_NAME ? getTerminalDefaultPrompts : getDefaultPrompts;

  console.log(
    "[PagePrompts] pathname:",
    pathname,
    "-> normalizedPath:",
    normalizedPath,
    "-> pageName:",
    pageName,
  );

  if (!pageName) {
    console.log(
      "[PagePrompts] No page_name mapping for:",
      pathname,
      "- using defaults",
    );
    return getDefaultPrompts();
  }

  try {
    const token = localStorage.getItem("token");
    const headers = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;

    console.log("[PagePrompts] Fetching prompts for:", pageName);

    const response = await fetch(`${API_BASE}/api/tara/chatbot/page-prompts/`, {
      method: "POST",
      headers,
      body: JSON.stringify({ page_name: pageName }),
    });

    if (!response.ok) {
      console.warn("[PagePrompts] Failed to fetch prompts:", response.status);
      return fallbackPrompts();
    }

    const data = await response.json();
    console.log(
      "[PagePrompts] Received response data:",
      JSON.stringify(data, null, 2),
    );

    // Transform backend format to component format
    if (data.prompts && Array.isArray(data.prompts)) {
      console.log(
        "[PagePrompts] Found prompts array with",
        data.prompts.length,
        "items",
      );
      const transformedPrompts = data.prompts
        .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
        .map((prompt) => ({
          text: prompt.question,
          icon: "/images/fluent_prompt-icon.svg",
          id: prompt.id,
          isBrandSpecific: prompt.is_brand_specific,
        }));
      console.log("[PagePrompts] Transformed prompts:", transformedPrompts);
      if (transformedPrompts.length === 0 && pageName === TERMINAL_PAGE_NAME) {
        return getTerminalDefaultPrompts();
      }
      return transformedPrompts;
    }

    // If data is directly an array of prompts
    if (Array.isArray(data)) {
      console.log(
        "[PagePrompts] Data is directly an array with",
        data.length,
        "items",
      );
      if (data.length === 0 && pageName === TERMINAL_PAGE_NAME) {
        return getTerminalDefaultPrompts();
      }
      return data.map((prompt) => ({
        text: prompt.question || prompt.text,
        icon: "/images/fluent_prompt-icon.svg",
        id: prompt.id,
        isBrandSpecific: prompt.is_brand_specific,
      }));
    }

    return fallbackPrompts();
  } catch (error) {
    console.error("[PagePrompts] Error fetching prompts:", error);
    return fallbackPrompts();
  }
}

/**
 * Normalize pathname for route matching
 */
function normalizePathname(pathname) {
  if (pathname.startsWith("/suspire-terminal")) {
    return "/suspire-terminal";
  }

  // Handle dynamic routes
  if (pathname.match(/^\/brands\/\d+/)) {
    return "/brands";
  }

  // Handle sustainability routes
  if (pathname.match(/^\/sustainability/)) {
    if (
      pathname === "/sustainability-progress" ||
      pathname === "/sustainability-certified"
    ) {
      return pathname;
    }
    return "/sustainability";
  }

  return pathname;
}

/**
 * Default fallback prompts
 */
function getDefaultPrompts() {
  return [
    {
      text: "How can I improve my visibility on Suspire?",
      icon: "/images/fluent_prompt-icon.svg",
    },
    {
      text: "Would 3SC help my brand stand out more?",
      icon: "/images/fluent_prompt-icon.svg",
    },
    {
      text: "Which Growth Suite activity fits my goal best?",
      icon: "/images/fluent_prompt-icon.svg",
    },
    {
      text: "What should I do with my current credits?",
      icon: "/images/fluent_prompt-icon.svg",
    },
    {
      text: "What is Suspire for Business?",
      icon: "/images/fluent_prompt-icon.svg",
    },
  ];
}

/**
 * Suspire Terminal prompts, used until admins configure their own for the
 * "suspire_terminal" page.
 */
function getTerminalDefaultPrompts() {
  return [
    "Where is this order right now?",
    "Walk me through this order's full timeline",
    "Which orders on this screen need attention?",
    "Which shipments are delayed or stuck in transit?",
    "Summarize what I'm looking at",
  ].map((text) => ({ text, icon: "/images/fluent_prompt-icon.svg" }));
}

/**
 * Get page name for current route (useful for logging/debugging)
 */
export function getPageNameForRoute(pathname) {
  const normalizedPath = normalizePathname(pathname);
  return ROUTE_TO_PAGE_NAME[normalizedPath] || "unknown";
}
